import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { Plus, Search, SlidersHorizontal, ArrowUp, Monitor, Smartphone } from 'lucide-react';
import { DottedLogo } from '../components/DottedLogo';
import { StartingPointSelector } from '../components/StartingPointSelector';
import { UseCaseCrossLinks } from '../components/UseCaseCrossLinks';
import { UseCaseTestimonials } from '../components/UseCaseTestimonials';
import { ClosingCTA } from '../components/ClosingCTA';
const TESTIMONIALS = [{
  name: 'Sarah Chen',
  role: 'CEO & Founder',
  avatar: 'https://i.pravatar.cc/100?img=47',
  quote: 'I used to spend Sunday nights digging through Slack and email to prep for Monday. Now Giga drops a briefing doc in my lap at 8 AM.'
}, {
  name: 'Marcus Weaver',
  role: '2x Founder',
  avatar: 'https://i.pravatar.cc/100?img=11',
  quote: 'The open loop sweep is a lifesaver. It caught an investor request I completely missed in a WhatsApp thread.'
}, {
  name: 'Elena Rostova',
  role: 'Chief of Staff',
  avatar: 'https://i.pravatar.cc/100?img=32',
  quote: "It's like having a co-pilot who has read every email, CRM note, and Slack message we've ever received."
}];
const ROTATING_CLAIMS = ['Runs your morning briefing', 'Triages your inbox', 'Books your travel', 'Reminds you to follow-up', 'Reschedules your week', 'Researches your competitors', 'Runs your errands', 'Preps you for interviews'];
const FEATURED_APPS = [{
  slug: 'hubspot',
  label: 'HubSpot'
}, {
  slug: 'salesforce',
  label: 'Salesforce'
}, {
  slug: 'slack',
  label: 'Slack'
}, {
  slug: 'gmail',
  label: 'Gmail'
}, {
  slug: 'google-calendar',
  label: 'Calendar'
}, {
  slug: 'whatsapp',
  label: 'WhatsApp'
}, {
  slug: 'amazon',
  label: 'Amazon'
}];
type GalleryCard = {
  id: string;
  kind: string;
  title: string;
  quote: string;
  gradient: string;
  icon: string;
  ui: React.ReactNode;
};
// Cards intentionally alternate Business → Personal → Business → … so the
// marquee oscillates between the two worlds as it scrolls.
const GALLERY_CARDS: GalleryCard[] = [{
  id: 'prep-sales-call',
  kind: 'Business',
  title: 'Sales calls, prepped',
  quote: '"Prep me for my Acme call tomorrow. Fetch the HubSpot deal, search Gmail for recent threads, and summarize the proposal."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'hubspot',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-5782">
        <div className="h-2 w-16 bg-neutral-200 rounded mb-3" data-id="element-5783" />
        <div className="flex justify-between items-center mb-2" data-id="element-5784">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-5785">
            Acme Corp
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-5786">$84k ARR</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-5787" />
        <div className="h-1.5 w-3/4 bg-neutral-100 rounded" data-id="element-5788" />
      </div>
}, {
  id: 'travel-booked',
  kind: 'Personal',
  title: 'Travel, booked',
  quote: '"Book my Friday trip to SF. Find a window seat under $400, a hotel near the office, and add it all to my calendar."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'skyscanner',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-5789">
        <div className="flex items-center justify-between mb-3" data-id="element-5790">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-5791">
            SFO · Fri
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-5792">$382</span>
        </div>
        <div className="flex items-center gap-2 mb-2" data-id="element-5793">
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-5794" />
          <div className="flex-1 h-px bg-neutral-200" data-id="element-5795" />
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-5796" />
        </div>
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-5797" />
      </div>
}, {
  id: 'board-decks',
  kind: 'Business',
  title: 'Board decks, updated',
  quote: '"Update the board deck with the latest metrics. Pull MRR from Stripe, burn from QuickBooks, and pipeline from Salesforce."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-5798">
        <div className="flex justify-between items-center mb-3" data-id="element-5799">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-5800">
            MRR Growth
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-5801">+18%</span>
        </div>
        <div className="h-16 w-full flex items-end gap-1" data-id="element-5802">
          {[40, 55, 45, 70, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-[#EA580C]/25 rounded-t-sm" style={{
        height: `${h}%`
      }} data-id="element-5803" />)}
        </div>
      </div>
}, {
  id: 'errands-handled',
  kind: 'Personal',
  title: 'Errands, handled',
  quote: '"Reorder my usual groceries, renew my car registration, and reschedule the dentist for next week. Confirm when each is done."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'amazon',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-5804">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-4" data-id="element-5805" />
        <div className="space-y-2.5" data-id="element-5806">
          {[true, true, false].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-5807">
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-5808" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-5809" />
            </div>)}
        </div>
      </div>
}, {
  id: 'inbox-triaged',
  kind: 'Business',
  title: 'Inbox, triaged',
  quote: '"Clear my inbox. Reply to anything routine, flag what needs me, and turn the rest into tasks so nothing slips."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'gmail',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-5810">
        <div className="flex items-center justify-between mb-3" data-id="element-5811">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-5812">Inbox</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-5813">3 left</span>
        </div>
        <div className="space-y-2" data-id="element-5814">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-5815">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-5816" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-5817" />
            </div>)}
        </div>
      </div>
}, {
  id: 'track-open-loops',
  kind: 'Personal',
  title: 'Open loops, closed',
  quote: '"Show me every open loop across work and life. Check my work email, personal email, Slack, and iMessage for anything I missed."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'slack',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-5818">
        <div className="h-2 w-24 bg-neutral-800 rounded mb-4" data-id="element-5819" />
        <div className="space-y-2.5" data-id="element-5820">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-5821">
              <div className="w-3 h-3 rounded-sm border border-neutral-300 shrink-0" data-id="element-5822" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-5823" />
            </div>)}
        </div>
      </div>
}];
function GeneratingStep({
  icon,
  text,
  index
}: {
  icon: string;
  text: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  return <div className="flex items-start gap-3" data-id="element-5824">
      <div className="relative w-6 h-6 shrink-0 mt-0.5" data-id="element-5825">
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: reduce ? 0 : index * 0.25
      }} className="absolute inset-0 rounded bg-white/10 flex items-center justify-center z-10" data-id="element-5826">
          <AppIcon slug={icon} className="w-3.5 h-3.5 object-contain" data-id="element-5827" />
        </motion.div>
        {!reduce && <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} whileInView={{
        opacity: [0, 1, 0],
        scale: [0.8, 1.4, 1.6]
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.8,
        delay: index * 0.25,
        ease: 'easeOut'
      }} className="absolute inset-0 rounded border border-[#EA580C]/40" data-id="element-5828" />}
      </div>

      <p className="text-sm text-neutral-300 leading-relaxed flex flex-wrap gap-x-[0.25em]" data-id="element-5829">
        {words.map((word, wIdx) => <motion.span key={wIdx} initial={{
        opacity: 0,
        y: 4
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : index * 0.25 + 0.1 + wIdx * 0.02
      }} className="inline-block" data-id="element-5830">
            {word}
          </motion.span>)}
      </p>
    </div>;
}
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className={className} onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.src.includes('simpleicons')) {
      img.src = `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-5831" />;
}
function GalleryJobCard({
  card
}: {
  card: GalleryCard;
}) {
  const isBusiness = card.kind === 'Business';
  return <div className="w-[280px] md:w-[320px] shrink-0 flex flex-col gap-6" data-id="element-5832">
      {/* Visual Area */}
      <div className={`h-[340px] rounded-3xl bg-gradient-to-br ${card.gradient} p-6 relative overflow-hidden flex flex-col items-center justify-center`} data-id="element-5833">
        <div className="relative z-10 flex flex-col items-center" data-id="element-5834">
          {card.ui}
          <div className="w-px h-12 bg-black/10 my-2" data-id="element-5835" />
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-sm border border-white flex items-center justify-center" data-id="element-5836">
            <AppIcon slug={card.icon} className="w-5 h-5 object-contain" data-id="element-5837" />
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-5838" />
      </div>

      {/* Text Area */}
      <div className="flex flex-col gap-3 px-2" data-id="element-5839">
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase" data-id="element-5840">
          <span className={`w-1.5 h-1.5 rounded-full ${isBusiness ? 'bg-[#EA580C]' : 'bg-[#2B211A]'}`} data-id="element-5841" />
          <span className={isBusiness ? 'text-[#EA580C]' : 'text-neutral-500'} data-id="element-5842">
            {card.kind}
          </span>
        </span>
        <h3 className="text-xl font-bold text-[#2B211A] -mt-1" data-id="element-5843">{card.title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-5844">{card.quote}</p>
      </div>
    </div>;
}
export function FoundersExecs() {
  const [claimIndex, setClaimIndex] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setClaimIndex(i => (i + 1) % ROTATING_CLAIMS.length);
    }, 2600);
    return () => clearInterval(id);
  }, [reduce]);
  return <>
      <section className="relative w-full bg-[#FAF8F4] text-[#2B211A] overflow-hidden" data-id="element-5845">
        {/* Cream dot-grid texture */}
        <div aria-hidden="true" className="cream-grid z-0" data-id="element-5846" />

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 pt-32 md:pt-40 pb-20 md:pb-28" data-id="element-5847">
          {/* Eyebrow */}
          <div className="text-center" data-id="element-5848">
            <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-5" data-id="element-5849">
              Founders & Execs
            </span>

            {/* Headline */}
            <h1 className="font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#2B211A]" data-id="element-5850">
              Your new assistant that
            </h1>

            {/* Rotating claim */}
            <div className="mt-4 h-[1.2em] font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#EA580C]" data-id="element-5851">
              <AnimatePresence mode="wait" data-id="element-5852">
                <motion.span key={claimIndex} initial={{
                opacity: 0,
                y: 14
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -14
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className="block" data-id="element-5853">
                  {ROTATING_CLAIMS[claimIndex]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* CTA */}
            <div className="mt-12 flex flex-col items-center gap-4" data-id="element-5854">
              <WaitlistCTA className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-5855">
                Get Started
              </WaitlistCTA>
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400" data-id="element-5856">
                Each Giga plan has a 7 day free trial.
              </p>
            </div>

            {/* Featured apps */}
            <div className="mt-16 pt-10 border-t border-[#EAE4D9] max-w-5xl mx-auto" data-id="element-5857">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-400 mb-6" data-id="element-5858">
                Featured Apps
              </p>
              <div className="flex flex-wrap md:flex-nowrap justify-center gap-3 md:gap-4" data-id="element-5859">
                {FEATURED_APPS.map(app => <div key={app.label} className="flex items-center gap-2.5 rounded-xl bg-white border border-[#EAE4D9] px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow" data-id="element-5860">
                    <AppIcon slug={app.slug} className="w-5 h-5 object-contain grayscale opacity-70" data-id="element-5861" />
                    <span className="text-sm font-bold text-[#2B211A]" data-id="element-5862">
                      {app.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Put Giga to work — job gallery */}
          <div className="mt-24 md:mt-32 w-full overflow-hidden" data-id="element-5863">
            <div className="text-center mb-12 max-w-2xl mx-auto" data-id="element-5864">
              <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-4" data-id="element-5865">
                Put Giga to work
              </span>
              <h2 className="font-title font-bold text-3xl md:text-4xl text-[#2B211A]" data-id="element-5866">
                Give Giga a job.
              </h2>
              <p className="mt-4 text-base md:text-lg text-neutral-500 leading-relaxed" data-id="element-5867">
                One assistant for both worlds — it moves between your business
                and your personal life without missing a beat.
              </p>
            </div>

            {/* Auto-scrolling marquee, oscillating Business ↔ Personal.
             Falls back to a manual horizontal scroll for reduced motion. */}
            {reduce ? <div className="flex overflow-x-auto gap-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-5868">
                {GALLERY_CARDS.map(card => <GalleryJobCard key={card.id} card={card} data-id="element-5869" />)}
              </div> : <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-id="element-5870">
                <motion.div className="flex gap-6 w-max" animate={{
              x: ['-50%', '0%']
            }} transition={{
              duration: 48,
              ease: 'linear',
              repeat: Infinity
            }} data-id="element-5871">
                  {[...GALLERY_CARDS, ...GALLERY_CARDS].map((card, i) => <GalleryJobCard key={`${card.id}-${i}`} card={card} data-id="element-5872" />)}
                </motion.div>
              </div>}
          </div>

          {/* Giga Does the Work comparison section */}
          <div className="mt-32 md:mt-40" data-id="element-5873">
            <div className="text-center mb-20" data-id="element-5874">
              <span className="inline-block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4" data-id="element-5875">
                Giga does the work
              </span>
              <h2 className="font-title font-bold text-4xl md:text-5xl text-[#2B211A]" data-id="element-5876">
                Other tools advise.{' '}
                <span className="text-[#EA580C]" data-id="element-5877">Giga executes.</span>
              </h2>
            </div>

            <div className="space-y-24" data-id="element-5878">
              {/* Row 1: MEETING PREP */}
              <div className="flex flex-col gap-8" data-id="element-5879">
                <div className="text-center max-w-3xl mx-auto" data-id="element-5880">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-5881">
                    Meeting Prep
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-5882">
                    Prep me for my Acme call tomorrow
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-5883">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-5884">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-5885">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-5886">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-5887">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-5888">
                      "Log into HubSpot, read the notes. Log into Gmail, search
                      the thread. Open Google Drive, find the proposal. Spend 30
                      minutes piecing it together..."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-5889">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-5890" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-5891">
                      <div className="flex items-center gap-2" data-id="element-5892">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-5893" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-5894" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-5895">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-5896" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-5897" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-5898">
                      {[{
                      icon: 'hubspot',
                      text: 'Fetching the Acme deal record and stage'
                    }, {
                      icon: 'gmail',
                      text: 'Searching recent threads for context'
                    }, {
                      icon: 'google-drive',
                      text: 'Finding the latest proposal draft'
                    }, {
                      icon: 'slack',
                      text: 'Drafting the briefing doc for the team'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-5899" />)}
                    </div>
                    <motion.div initial={reduce ? {
                    opacity: 1,
                    y: 0
                  } : {
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-50px'
                  }} transition={{
                    duration: 0.5,
                    delay: reduce ? 0 : 1.4
                  }} className="relative z-10 bg-[#EA580C] rounded-xl p-3 md:p-6 mt-auto shadow-xl shadow-orange-900/30" data-id="element-5900">
                      <span className="block text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1.5 md:mb-2" data-id="element-5901">
                        The payoff
                      </span>
                      <p className="text-sm md:text-xl font-bold text-white leading-snug" data-id="element-5902">
                        You walk into the meeting with the exact context you
                        need, zero digging required.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Row 2: OPEN LOOPS */}
              <div className="flex flex-col gap-8" data-id="element-5903">
                <div className="text-center max-w-3xl mx-auto" data-id="element-5904">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-5905">
                    Open Loops
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-5906">
                    What am I forgetting across work and life?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-5907">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-5908">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-5909">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-5910">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-5911">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-5912">
                      "You can't. You just have to remember everything, or spend
                      2 hours manually sweeping 4 inboxes and 3 chat apps."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-5913">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-5914" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-5915">
                      <div className="flex items-center gap-2" data-id="element-5916">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-5917" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-5918" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-5919">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-5920" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-5921" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-5922">
                      {[{
                      icon: 'gmail',
                      text: 'Scanning Work Email for promised follow-ups'
                    }, {
                      icon: 'google-calendar',
                      text: 'Checking Personal Calendar for family obligations'
                    }, {
                      icon: 'slack',
                      text: 'Searching Slack DMs and exec channels'
                    }, {
                      icon: 'apple',
                      text: 'Finding loose texts and quick asks'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-5923" />)}
                    </div>
                    <motion.div initial={reduce ? {
                    opacity: 1,
                    y: 0
                  } : {
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-50px'
                  }} transition={{
                    duration: 0.5,
                    delay: reduce ? 0 : 1.4
                  }} className="relative z-10 bg-[#EA580C] rounded-xl p-3 md:p-6 mt-auto shadow-xl shadow-orange-900/30" data-id="element-5924">
                      <span className="block text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1.5 md:mb-2" data-id="element-5925">
                        The payoff
                      </span>
                      <p className="text-sm md:text-xl font-bold text-white leading-snug" data-id="element-5926">
                        You never drop a ball again. Every open loop, surfaced
                        and prioritized.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pick the function that eats the most time, then see Giga handle it */}
      <StartingPointSelector data-id="element-5927" />

      {/* Role-relevant social proof right before the conversion moment */}
      <UseCaseTestimonials testimonials={TESTIMONIALS} data-id="element-5928" />

      {/* Keep visitors in the funnel: other use cases, then the conversion CTA */}
      <UseCaseCrossLinks current="founders-execs" data-id="element-5929" />
      <ClosingCTA data-id="element-5930" />
    </>;
}